/**
 * React Mounter for Production Shadow DOM SDK
 * 
 * Handles mounting React applications inside Shadow DOM containers
 * with proper context providers and component isolation.
 */

import React from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { shadowLogger } from '../utils/logger'
import type { ShadowEmbedConfig } from './types'

// Store React roots for cleanup
const reactRoots = new Map<ShadowRoot, Root>()

export class ShadowReactMounter {
  /**
   * Mount React application in Shadow DOM
   */
  static async mount(
    shadowRoot: ShadowRoot, 
    config: ShadowEmbedConfig,
    callbacks?: {
      onResize?: (height: number) => void
      onOpenLink?: (url: string) => void
      onCenterRequest?: (height: number) => void
    }
  ): Promise<void> {
    try {
      shadowLogger.debug('Mounting React application in Shadow DOM...')
      
      // Create app container
      const appContainer = document.createElement('div')
      appContainer.id = 'kwry-shadow-root'
      appContainer.className = 'kwry-shadow-app'
      appContainer.style.cssText = `
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
      `
      
      // Append to shadow root
      shadowRoot.appendChild(appContainer)
      
      // Create React root
      const root = createRoot(appContainer)
      reactRoots.set(shadowRoot, root)
      
      // Import and render Shadow DOM app component
      const { ShadowApp } = await import('./ShadowApp')
      
      // Create app with providers using React.createElement (no JSX)
      const App = React.createElement(ShadowApp, {
        config,
        onResize: callbacks?.onResize,
        onOpenLink: callbacks?.onOpenLink,
        onCenterRequest: callbacks?.onCenterRequest
      })
      
      // Render the app
      root.render(App)
      
      shadowLogger.debug('✅ React application mounted successfully in Shadow DOM')
      
    } catch (error) {
      shadowLogger.error('❌ Failed to mount React application:', error)
      throw error
    }
  }

  /**
   * Unmount React application
   */
  static unmount(shadowRoot: ShadowRoot): void {
    try {
      const root = reactRoots.get(shadowRoot)
      if (root) {
        root.unmount()
        reactRoots.delete(shadowRoot)
        shadowLogger.debug('✅ React application unmounted successfully')
      }
    } catch (error) {
      shadowLogger.error('❌ Failed to unmount React application:', error)
    }
  }

  /**
   * Check if React is available
   */
  static isReactAvailable(): boolean {
    try {
      return typeof React !== 'undefined' && typeof createRoot !== 'undefined'
    } catch {
      return false
    }
  }

  /**
   * Get all active React roots (for debugging)
   */
  static getActiveRoots(): Map<ShadowRoot, Root> {
    return new Map(reactRoots)
  }
}